// ANA-R2-07 (absorbs ANA-R2-08) — Tiles contextual action bar.
//
// Decision OQ-A2-3(c): the Promote-selected action does NOT live permanently
// in the resting toolbar. The default Tiles toolbar is grouped filters only
// (scope + content kind). A contextual action bar appears ONLY when ≥1 tile is
// selected, carrying the selection-scoped action(s) + a selection count + a
// clear-selection affordance, and disappears when the selection clears.
//
// This is the standard "selection action bar" (a.k.a. contextual action bar /
// bulk-action toolbar) pattern: macOS Finder, Gmail, GitHub's file list and
// Material Design's "contextual action bar" all surface batch actions only
// while a selection exists, keeping the resting chrome quiet. See the PR body
// for the short HIG note (AC5).
//
// AC4 (absorbs ANA-R2-08): the Promote button keeps the blue contained action
// treatment and is sized from the shared `compactButtonXsSx` preset
// (packages/ui/src/theme/compact.ts, UI-01) — same scale as the resting scope
// toggles — never a re-derived height.
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { compactButtonXsSx } from "@aeriosiq/ui";
import { useSelectionStore } from "../state/selectionStore.js";
import { TilePromoteButton } from "./TilePromoteButton.js";
import { XIcon } from "@aeriosiq/ui/icons";

export interface TilesActionBarProps {
  /** Inspection / data-acquisition id — forwarded to the promote flow. */
  inspectionId: string;
  /**
   * When false the surrounding capability gate refuses edits
   * (EVALUATION_DONE / LOCKED): the action bar still appears on selection so
   * the count + clear affordance work, but the Promote action is omitted.
   */
  editAllowed?: boolean;
}

/**
 * The contextual action bar. Renders nothing while the selection is empty
 * (AC1 — the resting toolbar has no action button); appears on the first
 * selected tile (AC2) and unmounts when the selection clears.
 */
export function TilesActionBar({
  inspectionId,
  editAllowed = true,
}: TilesActionBarProps): React.ReactElement | null {
  const selectedIds = useSelectionStore((s) => s.selectedFindingIds);
  const resetSelection = useSelectionStore((s) => s.reset);
  const count = selectedIds.size;

  if (count === 0) return null;

  return (
    <Box
      data-testid="tiles-action-bar"
      role="toolbar"
      aria-label="Selection actions"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.5,
        borderBottom: 1,
        borderColor: "divider",
        // A faint tonal-blue wash so the action bar reads as a distinct
        // selection-active surface above the grid (workspace-chrome rule:
        // tinted, not glass).
        bgcolor: (t) => `${t.palette.primary.main}0A`,
        flexShrink: 0,
      }}
    >
      <Tooltip title="Clear the current tile selection.">
        <Button
          size="small"
          variant="text"
          color="inherit"
          startIcon={<XIcon fontSize="small" />}
          onClick={resetSelection}
          sx={{ ...compactButtonXsSx }}
          data-testid="tiles-action-clear"
          aria-label="Clear selection"
        >
          Clear
        </Button>
      </Tooltip>
      <Typography
        variant="caption"
        data-testid="tiles-action-count"
        sx={{
          color: "text.secondary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count} selected
      </Typography>
      {editAllowed ? (
        <Box sx={{ ml: "auto" }}>
          <TilePromoteButton inspectionId={inspectionId} />
        </Box>
      ) : null}
    </Box>
  );
}
